import React, { useRef } from "react";
import {
  AdsConversionClient,
  AdsConversionSourceProvider,
} from "./ads_conversion";

import { GAClient, GASourceProvider } from "./ga";
import { GTMClient, GTMSourceProvider } from "./gtm";
import { HotjarClient, HotjarSourceProvider } from "./hotjar";
import { HubspotClient, HubspotSourceProvider } from "./hubspot";
import { PixelClient, PixelSourceProvider } from "./pixel";
import {
  Analytics,
  AnalyticsOptions,
  EcommerceEvent,
  GroupTraits,
  ScreenProperties,
  TrackProperties,
  UserTraits,
} from "./types";
import { MixpanelClient, MixpanelSourceProvider } from "./mixpanel";
import { LinkedInClient, LinkedInSourceProvider } from "./linkedin";
import { GA4Client, GA4SourceProvider } from "./ga4";
import { GoogleBookClient, GoogleBookSourceProvider } from "./google_book";
import { OneSignalClient, OneSignalSourceProvider } from "./onesignal";
import { CustomerioClient, CustomerioSourceProvider } from "./customerio";

export interface AnalyticsContext extends Analytics {
  // This function loads up a client but does not initialize it. This is specific to ga4
  // so that instead of initializing ga4 multiple times per client (which is not allowed),
  // we can preload flexspace client first and then initialize it later with a partner's ga4 client
  preloadClient: (client: AnalyticsClient) => void;
  addClient: (client: AnalyticsClient) => void;
  integration: { [provider: string]: Analytics };
  getSources: () => typeof sources;
}

export const AnalyticsContext = React.createContext<AnalyticsContext>({
  integration: {},
  preloadClient: () => {},
  addClient: () => {},
  group: () => {},
  register: () => {},
  identify: () => {},
  screen: () => {},
  event: () => {},
  ecommerceEvent: () => {},
  getSources: () => sources as typeof sources,
});

export const useAnalytics = () => {
  return React.useContext(AnalyticsContext);
};

export interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider = (props: AnalyticsProviderProps) => {
  const { children } = props;
  const analytics = useRef(new AnalyticsCombinator()).current;

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

const sources = {
  ga: new GASourceProvider(),
  gtm: new GTMSourceProvider(),
  hubspot: new HubspotSourceProvider(),
  hotjar: new HotjarSourceProvider(),
  pixel: new PixelSourceProvider(),
  ads_conversion: new AdsConversionSourceProvider(),
  mixpanel: new MixpanelSourceProvider(),
  linkedin: new LinkedInSourceProvider(),
  ga4: new GA4SourceProvider(),
  googleBook: new GoogleBookSourceProvider(),
  onesignal: new OneSignalSourceProvider(),
  customerio: new CustomerioSourceProvider(),
};

type AnalyticsClient =
  | GAClient
  | GTMClient
  | HubspotClient
  | HotjarClient
  | PixelClient
  | AdsConversionClient
  | MixpanelClient
  | LinkedInClient
  | GoogleBookClient
  | GA4Client
  | OneSignalClient
  | CustomerioClient;

export class AnalyticsCombinator {
  integration: { [provider: string]: Analytics } = {};

  preloadClient(client: AnalyticsClient) {
    let source = this.integration[client.provider];

    switch (client.provider) {
      case "ga4":
        source = sources.ga4.preload(client);
        break;
      default:
        console.error(client, "not supported");
        return;
    }

    this.integration[client.provider] = source;
  }

  addClient(client: AnalyticsClient) {
    let source = this.integration[client.provider];

    /**
     * we only need to initialize (except GA) client once because it sticks across all pages
     * it only lost on reloading page
     */
    if (
      client.provider !== "ga" &&
      client.provider !== "ga4" &&
      client.provider !== "onesignal" &&
      source
    ) {
      return;
    }
    switch (client.provider) {
      case "ga":
        source = sources.ga.initialize(client);
        break;
      case "gtm":
        source = sources.gtm.initialize(client);
        break;
      case "hubspot":
        source = sources.hubspot.initialize();
        break;
      case "hotjar":
        source = sources.hotjar.initialize(client);
        break;
      case "pixel":
        source = sources.pixel.initialize(client);
        break;
      case "ads_conversion":
        source = sources.ads_conversion.initialize(client);
        break;
      case "mixpanel":
        source = sources.mixpanel.initialize(client);
        break;
      case "linkedin":
        source = sources.linkedin.initialize(client);
        break;
      case "ga4":
        source = sources.ga4.initialize(client);
        break;
      case "google_book":
        source = sources.googleBook.initialize(client);
        break;
      case "onesignal":
        source = sources.onesignal.initialize(client);
        break;
      case "customerio":
        source = sources.customerio.initialize(client);
        break;
      default:
        console.error(client, "not supported");
        return;
    }

    this.integration[client.provider] = {
      ...source,
      sendPageView: client.sendPageView,
    };

    /**
     * Execute first page view after initialized
     */
    if (client.sendPageView) {
      source.screen();
    }
  }

  getSources() {
    return sources;
  }

  getIntegrations(options?: AnalyticsOptions) {
    const onlyProviders = options?.onlyProviders || [];

    // no provider specified, return all
    if (!onlyProviders.length) {
      return Object.values(this.integration);
    }

    const integrations: Analytics[] = [];

    for (const [key, value] of Object.entries(this.integration)) {
      // Don't care about provider not in the list
      if (!onlyProviders.includes(key)) {
        continue;
      }

      const client = value as Analytics;
      integrations.push(client);
    }

    return integrations;
  }

  group(groupID: string, traits?: GroupTraits) {
    this.getIntegrations().forEach((client) =>
      client.identify(groupID, traits)
    );
  }

  identify(userID: string, traits?: UserTraits, options?: AnalyticsOptions) {
    this.getIntegrations(options).forEach((client) =>
      client.identify(userID, traits)
    );
  }

  // Default will send screen event to all integrations
  // or send only for integrations specified in onlyProviders
  screen(
    name: string,
    properties?: ScreenProperties,
    options?: AnalyticsOptions
  ) {
    this.getIntegrations(options).forEach((client) =>
      client.screen(name, properties)
    );
  }

  ecommerceEvent(event: EcommerceEvent) {
    this.getIntegrations().forEach((client) => client.ecommerceEvent(event));
  }

  event(
    eventName: string,
    properties?: TrackProperties,
    options?: AnalyticsOptions
  ) {
    this.getIntegrations(options).forEach((client) =>
      client.event(eventName, properties)
    );
  }

  register(properties: TrackProperties, options?: AnalyticsOptions) {
    this.getIntegrations(options).forEach((client) =>
      client.register(properties)
    );
  }
}
